import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef } from "react";
import { Platform, View, type LayoutChangeEvent } from "react-native";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";
import { scheduleOnRN } from "react-native-worklets";
import { Image as ExpoImage, type ImageProps } from "expo-image";
import { type ImageSource } from "expo-image/src/Image.types";
import { clampScale, clampTranslation, isEffectivelyAtMinScale, MIN_SCALE, EPSILON } from "@src/utils/pinch-zoom";

const ZOOM_RESET_DURATION = 200;
const ZOOM_STEP_FACTOR = 1.5;
const DOUBLE_TAP_DELAY = 300;
const WHEEL_ZOOM_FACTOR = 0.003;

export interface ZoomableImageRef {
  resetZoom(animate?: boolean): void;
  zoomIn(): void;
  zoomOut(): void;
}

interface ZoomableImageProps {
  source: ImageSource | string;
  style?: object;
  contentFit?: ImageProps["contentFit"];
  onScaleChange?: (scale: number) => void;
}

export const ZoomableImage = forwardRef<ZoomableImageRef, ZoomableImageProps>(
  ({ source, style, contentFit = "scale-down", onScaleChange }, ref) => {
    const scale = useSharedValue(MIN_SCALE);
    const translateX = useSharedValue(0);
    const translateY = useSharedValue(0);
    const savedScale = useSharedValue(MIN_SCALE);
    const savedTranslateX = useSharedValue(0);
    const savedTranslateY = useSharedValue(0);
    const focalX = useSharedValue(0);
    const focalY = useSharedValue(0);

    const viewWidth = useSharedValue(0);
    const viewHeight = useSharedValue(0);

    const lastTapTime = useRef(0);
    const singleTapTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingTapX = useRef(0);
    const pendingTapY = useRef(0);

    const notifyScale = useCallback(
      (value: number) => {
        onScaleChange?.(Math.round(value * 100) / 100);
      },
      [onScaleChange],
    );

    const resetZoom = useCallback(
      (animate = true) => {
        if (singleTapTimer.current) {
          clearTimeout(singleTapTimer.current);
          singleTapTimer.current = null;
        }
        if (animate) {
          scale.value = withTiming(MIN_SCALE, { duration: ZOOM_RESET_DURATION });
          translateX.value = withTiming(0, { duration: ZOOM_RESET_DURATION });
          translateY.value = withTiming(0, { duration: ZOOM_RESET_DURATION }, () => {
            "worklet";
            scheduleOnRN(notifyScale, MIN_SCALE);
          });
        } else {
          scale.value = MIN_SCALE;
          translateX.value = 0;
          translateY.value = 0;
          notifyScale(MIN_SCALE);
        }
        savedScale.value = MIN_SCALE;
        savedTranslateX.value = 0;
        savedTranslateY.value = 0;
      },
      [scale, translateX, translateY, savedScale, savedTranslateX, savedTranslateY, notifyScale],
    );

    const zoomIn = useCallback(() => {
      const newScale = clampScale(scale.value * ZOOM_STEP_FACTOR);
      const clamped = clampTranslation(newScale, translateX.value, translateY.value, viewWidth.value, viewHeight.value);
      scale.value = withTiming(newScale, { duration: ZOOM_RESET_DURATION });
      translateX.value = withTiming(clamped.x, { duration: ZOOM_RESET_DURATION });
      translateY.value = withTiming(clamped.y, { duration: ZOOM_RESET_DURATION });
      savedScale.value = newScale;
      savedTranslateX.value = clamped.x;
      savedTranslateY.value = clamped.y;
      notifyScale(newScale);
    }, [
      scale,
      translateX,
      translateY,
      savedScale,
      savedTranslateX,
      savedTranslateY,
      viewWidth,
      viewHeight,
      notifyScale,
    ]);

    const zoomOut = useCallback(() => {
      const newScale = clampScale(scale.value / ZOOM_STEP_FACTOR);
      if (isEffectivelyAtMinScale(newScale)) {
        resetZoom();
        return;
      }
      const clamped = clampTranslation(newScale, translateX.value, translateY.value, viewWidth.value, viewHeight.value);
      scale.value = withTiming(newScale, { duration: ZOOM_RESET_DURATION });
      translateX.value = withTiming(clamped.x, { duration: ZOOM_RESET_DURATION });
      translateY.value = withTiming(clamped.y, { duration: ZOOM_RESET_DURATION });
      savedScale.value = newScale;
      savedTranslateX.value = clamped.x;
      savedTranslateY.value = clamped.y;
      notifyScale(newScale);
    }, [
      scale,
      translateX,
      translateY,
      savedScale,
      savedTranslateX,
      savedTranslateY,
      viewWidth,
      viewHeight,
      resetZoom,
      notifyScale,
    ]);

    useImperativeHandle(ref, () => ({ resetZoom, zoomIn, zoomOut }), [resetZoom, zoomIn, zoomOut]);

    const sourceKey = typeof source === "string" ? source : JSON.stringify(source);
    const prevSourceKey = useRef(sourceKey);
    useLayoutEffect(() => {
      if (prevSourceKey.current !== sourceKey) {
        prevSourceKey.current = sourceKey;
        resetZoom(false);
      }
    }, [sourceKey, resetZoom]);

    useEffect(() => {
      return () => {
        if (singleTapTimer.current) {
          clearTimeout(singleTapTimer.current);
        }
      };
    }, []);

    const handleLayout = useCallback(
      (event: LayoutChangeEvent) => {
        viewWidth.value = event.nativeEvent.layout.width;
        viewHeight.value = event.nativeEvent.layout.height;
      },
      [viewWidth, viewHeight],
    );

    const zoomInAtPoint = useCallback(
      (tapX: number, tapY: number) => {
        const w = viewWidth.value;
        const h = viewHeight.value;
        const currentScale = scale.value;

        const newScale = clampScale(currentScale * ZOOM_STEP_FACTOR);
        if (newScale === currentScale) return;

        const focusX = tapX - w / 2;
        const focusY = tapY - h / 2;
        const scaleRatio = newScale / currentScale;
        const rawTx = focusX * (1 - scaleRatio) + translateX.value * scaleRatio;
        const rawTy = focusY * (1 - scaleRatio) + translateY.value * scaleRatio;
        const clamped = clampTranslation(newScale, rawTx, rawTy, w, h);

        scale.value = withTiming(newScale, { duration: ZOOM_RESET_DURATION });
        translateX.value = withTiming(clamped.x, { duration: ZOOM_RESET_DURATION });
        translateY.value = withTiming(clamped.y, { duration: ZOOM_RESET_DURATION });
        savedScale.value = newScale;
        savedTranslateX.value = clamped.x;
        savedTranslateY.value = clamped.y;
        notifyScale(newScale);
      },
      [scale, translateX, translateY, savedScale, savedTranslateX, savedTranslateY, viewWidth, viewHeight, notifyScale],
    );

    const handleTap = useCallback(
      (x: number, y: number) => {
        const now = Date.now();

        if (now - lastTapTime.current < DOUBLE_TAP_DELAY) {
          if (singleTapTimer.current) {
            clearTimeout(singleTapTimer.current);
            singleTapTimer.current = null;
          }
          lastTapTime.current = 0;
          resetZoom();
        } else {
          lastTapTime.current = now;
          pendingTapX.current = x;
          pendingTapY.current = y;
          singleTapTimer.current = setTimeout(() => {
            zoomInAtPoint(pendingTapX.current, pendingTapY.current);
            singleTapTimer.current = null;
          }, DOUBLE_TAP_DELAY);
        }
      },
      [resetZoom, zoomInAtPoint],
    );

    const handleWheel = useCallback(
      (event: { deltaY: number; offsetX: number; offsetY: number; preventDefault: () => void }) => {
        event.preventDefault();
        const currentScale = scale.value;
        const proposed = currentScale * (1 - event.deltaY * WHEEL_ZOOM_FACTOR);
        const newScale = clampScale(proposed);

        if (isEffectivelyAtMinScale(newScale)) {
          scale.value = newScale;
          translateX.value = 0;
          translateY.value = 0;
        } else {
          const w = viewWidth.value;
          const h = viewHeight.value;
          const focusX = event.offsetX - w / 2;
          const focusY = event.offsetY - h / 2;
          const scaleRatio = newScale / currentScale;
          const rawTx = focusX * (1 - scaleRatio) + translateX.value * scaleRatio;
          const rawTy = focusY * (1 - scaleRatio) + translateY.value * scaleRatio;
          const clamped = clampTranslation(newScale, rawTx, rawTy, w, h);
          scale.value = newScale;
          translateX.value = clamped.x;
          translateY.value = clamped.y;
        }

        savedScale.value = newScale;
        savedTranslateX.value = translateX.value;
        savedTranslateY.value = translateY.value;
        notifyScale(newScale);
      },
      [scale, translateX, translateY, savedScale, savedTranslateX, savedTranslateY, viewWidth, viewHeight, notifyScale],
    );

    const tapGesture = Gesture.Tap()
      .maxDuration(500)
      .onEnd((event) => {
        "worklet";
        scheduleOnRN(handleTap, event.x, event.y);
      });

    const pinchGesture = Gesture.Pinch()
      .onStart((event) => {
        "worklet";
        savedScale.value = scale.value;
        savedTranslateX.value = translateX.value;
        savedTranslateY.value = translateY.value;
        focalX.value = event.focalX - viewWidth.value / 2;
        focalY.value = event.focalY - viewHeight.value / 2;
      })
      .onUpdate((event) => {
        "worklet";
        const proposed = savedScale.value * event.scale;
        const newScale = clampScale(proposed);
        scale.value = newScale;

        if (isEffectivelyAtMinScale(newScale)) {
          translateX.value = 0;
          translateY.value = 0;
        } else {
          const scaleRatio = newScale / savedScale.value;
          const rawTx = focalX.value * (1 - scaleRatio) + savedTranslateX.value * scaleRatio;
          const rawTy = focalY.value * (1 - scaleRatio) + savedTranslateY.value * scaleRatio;
          const clamped = clampTranslation(newScale, rawTx, rawTy, viewWidth.value, viewHeight.value);
          translateX.value = clamped.x;
          translateY.value = clamped.y;
        }
      })
      .onEnd(() => {
        "worklet";
        if (isEffectivelyAtMinScale(scale.value)) {
          scale.value = withTiming(MIN_SCALE, { duration: ZOOM_RESET_DURATION });
          translateX.value = withTiming(0, { duration: ZOOM_RESET_DURATION });
          translateY.value = withTiming(0, { duration: ZOOM_RESET_DURATION });
        }
        savedScale.value = scale.value;
        savedTranslateX.value = translateX.value;
        savedTranslateY.value = translateY.value;
        scheduleOnRN(notifyScale, scale.value);
      });

    const panGesture = Gesture.Pan()
      .minPointers(Platform.OS === "web" ? 1 : 2)
      .activeOffsetX([-10, 10])
      .activeOffsetY([-10, 10])
      .onStart(() => {
        "worklet";
        savedTranslateX.value = translateX.value;
        savedTranslateY.value = translateY.value;
      })
      .onUpdate((event) => {
        "worklet";
        if (scale.value <= MIN_SCALE + EPSILON) return;
        const clamped = clampTranslation(
          scale.value,
          savedTranslateX.value + event.translationX,
          savedTranslateY.value + event.translationY,
          viewWidth.value,
          viewHeight.value,
        );
        translateX.value = clamped.x;
        translateY.value = clamped.y;
      })
      .onEnd(() => {
        "worklet";
        savedTranslateX.value = translateX.value;
        savedTranslateY.value = translateY.value;
      });

    const composedGesture = Gesture.Race(tapGesture, Gesture.Simultaneous(pinchGesture, panGesture));

    const animatedStyle = useAnimatedStyle(() => ({
      transform: [{ translateX: translateX.value }, { translateY: translateY.value }, { scale: scale.value }],
    }));

    const containerRef = useRef<View>(null);

    useEffect(() => {
      if (Platform.OS !== "web") return;
      const node = containerRef.current as unknown as HTMLElement | null;
      if (!node) return;
      const handler = (e: WheelEvent) => {
        e.preventDefault();
        const rect = node.getBoundingClientRect();
        handleWheel({
          deltaY: e.deltaY,
          offsetX: e.clientX - rect.left,
          offsetY: e.clientY - rect.top,
          preventDefault: () => {},
        });
      };
      node.addEventListener("wheel", handler, { passive: false });
      return () => node.removeEventListener("wheel", handler);
    }, [handleWheel]);

    const webDragProps =
      Platform.OS === "web" ?
        ({
          onDragStart: (e: { preventDefault: () => void }) => e.preventDefault(),
          draggable: false,
        } as unknown as Record<string, unknown>)
      : undefined;

    return (
      <View ref={containerRef} style={{ width: "100%", height: "100%" }} onLayout={handleLayout}>
        <GestureDetector gesture={composedGesture}>
          <Animated.View
            style={[{ width: "100%", height: "100%", userSelect: "none" }, animatedStyle]}
            {...webDragProps}
          >
            <ExpoImage
              source={source}
              style={[style ?? { width: "100%", height: "100%" }, { pointerEvents: "none" }]}
              contentFit={contentFit}
            />
          </Animated.View>
        </GestureDetector>
      </View>
    );
  },
);

ZoomableImage.displayName = "ZoomableImage";
